import type {
  BankMovementAccountsQueryParams,
  BankMovementFusionCandidatesQueryParams,
  BankMovementPaymentModesQueryParams,
  BankMovementsQueryParams,
  LedgerAllocationCategorizationRulesQueryParams,
  LedgerAllocationsQueryParams,
  SepaRemittancesQueryParams,
  SettlementsQueryParams,
  SynthesisQueryParams,
  SynthesisDetailQueryParams,
  AccountingBudgetsQueryParams,
} from "../types/accounting.types";

export const ACCOUNTING_QUERY_KEYS = {
  all: ["accounting"] as const,
  bankAccounts: ["accounting", "bank-accounts"] as const,
  bankAccountsList: () =>
    [...ACCOUNTING_QUERY_KEYS.bankAccounts, "list"] as const,
  bridgeAccounts: ["accounting", "bridge-accounts"] as const,
  bridgeAccountsList: () =>
    [...ACCOUNTING_QUERY_KEYS.bridgeAccounts, "list"] as const,
  bridgeProviders: ["accounting", "bridge-providers"] as const,
  bridgeProvidersList: (q: string) =>
    [...ACCOUNTING_QUERY_KEYS.bridgeProviders, "list", { q }] as const,
  bankMovements: ["accounting", "bank-movements"] as const,
  // DEC-15 : le compteur agrégé des écarts en attente d'arbitrage. Hors de
  // `bankMovementsList` : il porte sur toute l'organisation, pas sur la vue.
  bankMovementsDiscrepancySummary: ["accounting", "bank-movements", "discrepancy-summary"] as const,
  // Imbriquée sous `bankMovements`, comme le résumé : un arbitrage de doublon
  // supprime un mouvement, donc la liste et cette file bougent ensemble.
  bankMovementDuplicateCandidates: ["accounting", "bank-movements", "duplicate-candidates"] as const,
  bankMovementsList: (params: BankMovementsQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.bankMovements, "list", params] as const,
  bankMovementFusionCandidates: ["accounting", "bank-movement-fusion-candidates"] as const,
  bankMovementFusionCandidatesList: (params: BankMovementFusionCandidatesQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.bankMovementFusionCandidates, "list", params] as const,
  bankMovementAccounts: ["accounting", "bank-movement-accounts"] as const,
  bankMovementAccountsList: (params: BankMovementAccountsQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.bankMovementAccounts, "list", params] as const,
  bankMovementDetail: (id: string) =>
    [...ACCOUNTING_QUERY_KEYS.bankMovements, "detail", id] as const,
  bankMovementPaymentModes: ["accounting", "bank-movement-payment-modes"] as const,
  bankMovementPaymentModesList: (params: BankMovementPaymentModesQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.bankMovementPaymentModes, "list", params] as const,
  ledgerAllocations: ["accounting", "ledger-allocations"] as const,
  accountingTags: ["accounting", "tags"] as const,
  accountingTagsList: (target: string) => [...ACCOUNTING_QUERY_KEYS.accountingTags, target] as const,
  ledgerAllocationsList: (params: LedgerAllocationsQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "list", params] as const,
  ledgerAllocationCategorizationRules: ["accounting", "ledger-allocation-categorization-rules"] as const,
  ledgerAllocationCategorizationRulesList: (params: LedgerAllocationCategorizationRulesQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.ledgerAllocationCategorizationRules, "list", params] as const,
  // YTI-1732 : les fiches de remise SEPA. Invalidées avec les mouvements : une
  // régénération ou une annulation touche les deux.
  sepaRemittances: ["accounting", "sepa-remittances"] as const,
  sepaEligibility: (bankAccountId: number) => [...ACCOUNTING_QUERY_KEYS.bankMovements, "sepa-eligibility", bankAccountId] as const,
  sepaRemittancesList: (params: SepaRemittancesQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.sepaRemittances, "list", params] as const,
  sepaRemittanceDetail: (id: string) =>
    [...ACCOUNTING_QUERY_KEYS.sepaRemittances, "detail", id] as const,
  settlements: ["accounting", "settlements"] as const,
  settlementsList: (params: SettlementsQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.settlements, "list", params] as const,
  settlementDetail: (id: string) =>
    [...ACCOUNTING_QUERY_KEYS.settlements, "detail", id] as const,
  settlementAccounts: ["accounting", "settlement-accounts"] as const,
  settlementAccountsList: (params: { context?: string } = {}) =>
    [...ACCOUNTING_QUERY_KEYS.settlementAccounts, "list", params] as const,
  settlementPaymentMethods: ["accounting", "settlement-payment-methods"] as const,
  settlementAllocations: ["accounting", "settlement-allocations"] as const,
  settlementAllocationsList: (settlementId: string) =>
    ["accounting", "settlement-allocations", "list", settlementId] as const,
  synthesis: ["accounting", "synthesis"] as const,
  synthesisList: (params: SynthesisQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.synthesis, "data", params] as const,
  synthesisDetail: (params: SynthesisDetailQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.synthesis, "detail", params] as const,
  entryTemplates: ["accounting", "entry-templates"] as const,
  entryTemplatesList: () =>
    ["accounting", "entry-templates", "list"] as const,
  // ADM-621 : budgets prévisionnels, paramètres comptables et catégories
  // org-scoped (page Budget).
  budgets: ["accounting", "budgets"] as const,
  budgetsList: (params: AccountingBudgetsQueryParams) =>
    [...ACCOUNTING_QUERY_KEYS.budgets, "list", params] as const,
  settings: ["accounting", "settings"] as const,
  orgOperationTypes: ["accounting", "org-operation-types"] as const,
  orgOperationTypesList: (params: { budgetType?: string | null, actif?: boolean }) =>
    [...ACCOUNTING_QUERY_KEYS.orgOperationTypes, "list", params] as const,
} as const;
